import { Globe, MessageSquare, Palette, Sparkles, Zap } from "lucide-react";
import { StaxGreeting } from "./StaxGreeting";

interface AuthLayoutProps {
  children: React.ReactNode;
  oauthContext?: boolean;
}

const features = [
  {
    icon: MessageSquare,
    text: "Build your site through conversation",
  },
  {
    icon: Palette,
    text: "Beautiful templates, colors, and fonts",
  },
  {
    icon: Globe,
    text: "Your own subdomain, live in seconds",
  },
  {
    icon: Zap,
    text: "Works with ChatGPT",
  },
  {
    icon: Sparkles,
    text: "100% free to get started",
  },
];

export function AuthLayout({ children, oauthContext }: AuthLayoutProps) {
  return (
    <div className="flex min-h-dvh">
      {/* Left panel - Stax & value prop (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-col justify-between gap-8 bg-zinc-50 border-r border-zinc-200 p-10 text-zinc-900">
        <div className="flex items-center gap-3">
          <img
            src="/icons/icon-192x192.png"
            alt="Stack.je"
            className="w-10 h-10 rounded-lg"
          />
          <span className="text-xl font-semibold tracking-tight">
            Stack.je
          </span>
        </div>

        <StaxGreeting layout="column" size={176} />

        <div>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-4 tracking-tight">
            Your website,
            <br />
            built by conversation.
          </h1>
          <p className="text-zinc-500 text-lg leading-relaxed max-w-sm">
            Describe what you want. Stax handles the design, hosting, and
            publishing. No code, no drag-and-drop, no fuss.
          </p>
        </div>

        <div className="hidden space-y-4 [@media(min-height:900px)]:block">
          {features.map((feature) => (
            <div key={feature.text} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                <feature.icon className="w-4 h-4 text-orange-700" />
              </div>
              <span className="text-zinc-600 text-sm">{feature.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 bg-zinc-50 border-b border-zinc-200 px-6 pt-5 pb-4 text-zinc-900">
          <img
            src="/icons/icon-192x192.png"
            alt="Stack.je"
            className="w-8 h-8 rounded-lg"
          />
          <div>
            <span className="text-lg font-semibold tracking-tight">
              Stack.je
            </span>
            <p className="text-zinc-500 text-xs">
              Your website, built by conversation.
            </p>
          </div>
        </div>

        {/* OAuth context banner */}
        {oauthContext && (
          <div className="bg-orange-50 border-b border-orange-100 px-6 py-3">
            <p className="text-sm text-orange-800 text-center">
              Connect your Stack.je account to continue building in ChatGPT
            </p>
          </div>
        )}

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-gray-50/50">
          <div className="w-full max-w-sm">
            {/* Stax introduces itself here on small screens, where the left panel is hidden */}
            <div className="lg:hidden mb-8">
              <StaxGreeting layout="row" size={104} />
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
